import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { cn } from '../utils/cn';

type FieldType = 'string' | 'number' | 'boolean' | 'json';

interface MetadataField {
  id: string;
  type: FieldType;
  key: string;
  value: string;
}

interface FieldError {
  key?: string;
  value?: string;
}

interface JsonMetadataBuilderProps {
  value: string;
  onChange: (json: string) => void;
}

const FIELD_TYPES: { type: FieldType; label: string }[] = [
  { type: 'string', label: 'Text' },
  { type: 'number', label: 'Number' },
  { type: 'boolean', label: 'Boolean' },
  { type: 'json', label: 'JSON' },
];

let fieldCounter = 0;
const nextId = () => `field_${++fieldCounter}_${Date.now()}`;

function parseJsonToFields(json: string): MetadataField[] {
  if (!json.trim()) return [];
  try {
    const parsed = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return [];
    return Object.entries(parsed).map(([key, val]) => {
      let type: FieldType = 'string';
      let value = '';
      if (typeof val === 'string') {
        type = 'string';
        value = val;
      } else if (typeof val === 'number') {
        type = 'number';
        value = String(val);
      } else if (typeof val === 'boolean') {
        type = 'boolean';
        value = String(val);
      } else {
        type = 'json';
        value = JSON.stringify(val);
      }
      return { id: nextId(), type, key, value };
    });
  } catch {
    return [];
  }
}

function fieldsToJson(fields: MetadataField[]): string {
  if (fields.length === 0) return '';
  const obj: Record<string, unknown> = {};
  for (const field of fields) {
    if (!field.key.trim()) continue;
    switch (field.type) {
      case 'string':
        obj[field.key] = field.value;
        break;
      case 'number': {
        const num = Number(field.value);
        obj[field.key] = isNaN(num) ? field.value : num;
        break;
      }
      case 'boolean':
        obj[field.key] = field.value === 'true';
        break;
      case 'json':
        try {
          obj[field.key] = JSON.parse(field.value);
        } catch {
          obj[field.key] = field.value;
        }
        break;
    }
  }
  return Object.keys(obj).length > 0 ? JSON.stringify(obj) : '';
}

function validateField(field: MetadataField, allFields: MetadataField[]): FieldError {
  const errors: FieldError = {};

  if (!field.key.trim()) {
    errors.key = 'Key is required';
  } else {
    const dupes = allFields.filter(f => f.id !== field.id && f.key.trim() === field.key.trim());
    if (dupes.length > 0) errors.key = 'Duplicate key';
  }

  if (field.type === 'number' && field.value.trim()) {
    if (isNaN(Number(field.value))) errors.value = 'Not a valid number';
  }

  if (field.type === 'json' && field.value.trim()) {
    try {
      JSON.parse(field.value);
    } catch {
      errors.value = 'Invalid JSON';
    }
  }

  return errors;
}

/* Compact 32px controls built from the shared input primitive (native select chevron included). */
const COMPACT_INPUT = 'glass-input min-h-0 h-8 py-1 px-2.5 text-xs w-full';
const COMPACT_SELECT = 'glass-input min-h-0 h-8 py-1 pl-2 text-xs w-full';

export const JsonMetadataBuilder = ({ value, onChange }: JsonMetadataBuilderProps) => {
  const [fields, setFields] = useState<MetadataField[]>(() => parseJsonToFields(value));
  const [errors, setErrors] = useState<Record<string, FieldError>>({});
  const isExternalUpdate = useRef(false);
  const prevJsonRef = useRef(value);

  useEffect(() => {
    if (value !== prevJsonRef.current) {
      isExternalUpdate.current = true;
      const parsed = parseJsonToFields(value);
      setFields(parsed);
      prevJsonRef.current = value;
    }
  }, [value]);

  const emitChange = useCallback((updatedFields: MetadataField[]) => {
    const newErrors: Record<string, FieldError> = {};
    updatedFields.forEach(f => {
      const err = validateField(f, updatedFields);
      if (err.key || err.value) newErrors[f.id] = err;
    });
    setErrors(newErrors);

    const json = fieldsToJson(updatedFields);
    prevJsonRef.current = json;
    onChange(json);
  }, [onChange]);

  const updateFields = useCallback((updater: (prev: MetadataField[]) => MetadataField[]) => {
    setFields(prev => {
      const next = updater(prev);
      emitChange(next);
      return next;
    });
  }, [emitChange]);

  const addField = () => {
    updateFields(prev => [...prev, { id: nextId(), type: 'string', key: '', value: '' }]);
  };

  const removeField = (id: string) => {
    updateFields(prev => prev.filter(f => f.id !== id));
  };

  const updateField = (id: string, patch: Partial<MetadataField>) => {
    updateFields(prev =>
      prev.map(f => f.id === id ? { ...f, ...patch } : f)
    );
  };

  const hasAnyErrors = Object.keys(errors).length > 0;

  return (
    <div className="space-y-2">
      {fields.length > 0 && (
        <div className="space-y-1.5">
          {/* Header row */}
          <div className="grid grid-cols-[90px_1fr_1fr_32px] gap-1.5 px-1">
            <span className="eyebrow">Type</span>
            <span className="eyebrow">Key</span>
            <span className="eyebrow">Value</span>
            <span />
          </div>

          {fields.map((field) => {
            const fieldErrors = errors[field.id];
            return (
              <div
                key={field.id}
                className="grid grid-cols-[90px_1fr_1fr_32px] gap-1.5 items-start group"
              >
                {/* Type selector */}
                <div>
                  <select
                    value={field.type}
                    onChange={(e) => {
                      const newType = e.target.value as FieldType;
                      const patch: Partial<MetadataField> = { type: newType };
                      if (newType === 'boolean' && field.value !== 'true' && field.value !== 'false') {
                        patch.value = 'true';
                      }
                      updateField(field.id, patch);
                    }}
                    className={COMPACT_SELECT}
                    aria-label="Field type"
                  >
                    {FIELD_TYPES.map(ft => (
                      <option key={ft.type} value={ft.type}>{ft.label}</option>
                    ))}
                  </select>
                </div>

                {/* Key input */}
                <div>
                  <input
                    type="text"
                    value={field.key}
                    onChange={(e) => updateField(field.id, { key: e.target.value })}
                    placeholder="key"
                    aria-label="Field key"
                    className={cn(
                      COMPACT_INPUT,
                      'font-mono',
                      fieldErrors?.key && 'border-red-500/50'
                    )}
                  />
                  {fieldErrors?.key && (
                    <p className="text-[11px] text-red-400 mt-1 px-0.5">{fieldErrors.key}</p>
                  )}
                </div>

                {/* Value input */}
                <div>
                  {field.type === 'boolean' ? (
                    <div>
                      <select
                        value={field.value}
                        onChange={(e) => updateField(field.id, { value: e.target.value })}
                        className={COMPACT_SELECT}
                        aria-label="Field value"
                      >
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    </div>
                  ) : (
                    <input
                      type={field.type === 'number' ? 'text' : 'text'}
                      value={field.value}
                      onChange={(e) => updateField(field.id, { value: e.target.value })}
                      aria-label="Field value"
                      placeholder={
                        field.type === 'number' ? '0'
                          : field.type === 'json' ? '["a", "b"] or {"nested": true}'
                          : 'value'
                      }
                      className={cn(
                        COMPACT_INPUT,
                        field.type === 'json' || field.type === 'number' ? 'font-mono' : '',
                        fieldErrors?.value && 'border-red-500/50'
                      )}
                    />
                  )}
                  {fieldErrors?.value && (
                    <p className="text-[11px] text-red-400 mt-1 px-0.5">{fieldErrors.value}</p>
                  )}
                </div>

                {/* Delete button */}
                <button
                  type="button"
                  onClick={() => removeField(field.id)}
                  className="btn-icon btn-icon-sm h-8 w-8 text-glass-500 hover:text-red-400 hover:bg-red-500/10"
                  title="Remove field"
                  aria-label="Remove field"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add field button */}
      <button
        type="button"
        onClick={addField}
        className={cn(
          "w-full h-8 rounded-lg border border-dashed text-xs font-medium flex items-center justify-center gap-1.5 transition-colors",
          "border-glass-200/20 text-glass-400 hover:border-accent-400/50 hover:text-accent-300 hover:bg-accent-500/5"
        )}
      >
        <Plus className="w-3.5 h-3.5" />
        Add field
      </button>

      {/* Validation summary */}
      {hasAnyErrors && fields.length > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-amber-400 px-0.5">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          <span>Fix field errors above before saving</span>
        </div>
      )}

      {/* JSON preview */}
      {fields.length > 0 && (
        <details className="group/preview">
          <summary className="text-[11px] text-glass-500 hover:text-glass-300 cursor-pointer transition-colors select-none">
            Preview JSON
          </summary>
          <pre className="subcard mt-1.5 p-2.5 text-[11px] text-glass-400 overflow-x-auto font-mono max-h-32 overflow-y-auto">
            {(() => {
              const json = fieldsToJson(fields);
              if (!json) return '{}';
              try {
                return JSON.stringify(JSON.parse(json), null, 2);
              } catch {
                return json;
              }
            })()}
          </pre>
        </details>
      )}
    </div>
  );
};
